import { Conflict, PlanningHelperService } from '../../services/planning-helper.service';
import {
  ChangeDetectionStrategy,
  Component,
  WritableSignal,
  signal,
  computed,
  OnInit,
  ChangeDetectorRef,
  effect,
  OnDestroy,
  ViewChild,
  inject,
  input,
  output,
} from '@angular/core';
import {
  DxButtonModule,
  DxTextAreaModule,
  DxTextBoxModule,
  DxFormModule,
  DxCheckBoxModule,
  DxDropDownButtonModule,
  DxPopupModule,
  DxMapComponent,
} from 'devextreme-angular';
import { ProjectDto } from '../../shared/generated/backend';
import { CommonModule } from '@angular/common';
import { DxMapModule } from 'devextreme-angular';
import { SharedModule } from '../../shared/shared.module';
import { TranslateService } from '@ngx-translate/core';
import {
  BranchService,
  FleetService,
  ProjectService,
  ConflictDto,
  ConflictType,
  NotificationState,
  AssignmentService,
} from '../../shared/generated/backend';
import { Subscription, firstValueFrom } from 'rxjs';
import notify from 'devextreme/ui/notify';
import { ContextService } from '../../services/context.service';
import { RouteMode } from 'devextreme/ui/map';
import { MapService } from '../../services/map.service';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import { VehicleScheduleRefreshService } from '../../services/vehicle-schedule-refresh.service';
import { NotificationService } from '../../shared/services/notification.service';
import { createBusyState, createKeyedBusyState } from '../../shared/util/busy-state';

export interface Marker {
  location: string | [number, number];
  color: string;
  iconSrc: string;
  height: number;
}

export interface Route {
  weight: number;
  color: string;
  opacity: number;
  mode: RouteMode;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  locations: any[];
}

interface Action {
  id: number;
  text: string;
  icon: string;
  onClick?: () => void;
  disabled?: boolean;
}

const ACTION_NOTIFY_DRIVER = 3;
const ACTION_DELETE_PROJECT = 4;
const ACTION_NOTIFY_OM = 5;

@Component({
  selector: 'app-project-footer-panel',
  imports: [
    SharedModule,
    CommonModule,
    DxTextBoxModule,
    DxTextAreaModule,
    DxButtonModule,
    DxFormModule,
    DxMapModule,
    DxCheckBoxModule,
    DxDropDownButtonModule,
    DxPopupModule,
  ],
  templateUrl: './project-footer-panel.component.html',
  styleUrl: './project-footer-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectFooterPanelComponent implements OnInit, OnDestroy {
  readonly NICLEN_HOME = '51.5265, 7.6005';
  readonly ZOOM_DEFAULT = 10;
  readonly ZOOM_NEAR = 20;

  readonly project = input<WritableSignal<ProjectDto | undefined>>(signal(undefined));
  readonly changed = output<string>();

  @ViewChild(DxMapComponent, { static: false }) map!: DxMapComponent;
  showMap: boolean = false;
  readonly showOmInfo = signal(false);

  isMapMaximized = false;
  private isOpeningMap = false;
  private mapLoadedForProjectNo?: number;
  isResettingMap = false;
  homeRoute: boolean = true;
  markers: Marker[] = [];
  routes: Route[] = [];
  routeDistances: number[] = [];
  readonly mapUpdate = createBusyState();
  /** Guards each mutating action of the drop-down (notify driver, delete project, notify OM) by its action id. */
  readonly actionBusy = createKeyedBusyState<number>();
  readonly actions = signal<Action[]>([]);
  public connectionString: string = '';

  async toggleMapSize() {
    if (this.isMapMaximized) {
      this.isMapMaximized = false;
      this.routeDistances = [];
      return;
    }

    await this.openMap();
  }

  private async openMap() {
    if (this.isOpeningMap) return;
    this.isOpeningMap = true;

    try {
      const p = this.project()();
      if (!p) return;

      this.isMapMaximized = true;

      this.homeRoute = true;
      this.mapService.setHomeRoute(true);

      const mustReload = this.mapLoadedForProjectNo !== p.ProjectNo;
      if (mustReload) {
        const loadunits = await firstValueFrom(this.projectService.getProjectLoadunits(p.ProjectNo));
        this.mapService.setContext(loadunits, p.ProjectNo);
        await this.mapService.updateMap(undefined, undefined, true, true);
        this.mapLoadedForProjectNo = p.ProjectNo;
      } else {
        await this.mapService.calculateRouteDistanceInfo();
      }

      await this.waitForMapReady();
    } finally {
      this.isOpeningMap = false;
    }
  }

  private async waitForMapReady(): Promise<void> {
    await firstValueFrom(
      this.mapService.mapReady$.pipe(
        distinctUntilChanged(),
        filter(r => r)
      )
    );
    this.resetMapZoom();
  }

  async toggleHomeRoute() {
    await this.mapUpdate.run(async () => {
      try {
        this.homeRoute = !this.homeRoute;
        this.mapService.setHomeRoute(this.homeRoute);

        const p = this.project()();
        if (!p) return;
        const loadunits = await firstValueFrom(this.projectService.getProjectLoadunits(p.ProjectNo));

        this.mapService.setContext(loadunits, p.ProjectNo);
        const startSeq = await firstValueFrom(this.mapService.readySeq$.pipe(distinctUntilChanged()));

        const nextReadyPromise = firstValueFrom(
          this.mapService.readySeq$.pipe(
            distinctUntilChanged(),
            filter(seq => seq > startSeq),
            take(1)
          )
        );

        await this.mapService.updateMap(undefined, undefined, this.homeRoute, this.isMapMaximized);
        await nextReadyPromise;
      } catch (err) {
        console.error('toggleHomeRoute failed', err);
      }
    });
  }

  async resetMapZoom() {
    if (this.isResettingMap) return;
    this.isResettingMap = true;

    try {
      // Ensure autoAdjust is disabled first
      this.map.instance.option('autoAdjust', false);

      // Wait for map to be fully rendered with markers
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

      // Enable autoAdjust to fit all markers and routes
      this.map.instance.option('autoAdjust', true);

      // Wait for autoAdjust to complete
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
    } finally {
      this.isResettingMap = false;
    }
  }

  onMapReady() {
    if (this.isMapMaximized && this.mapLoadedForProjectNo) {
      this.resetMapZoom();
    }
  }

  // computed, so the drop-down only gets a new items array when the actions, the OM info or a busy state change
  readonly filteredActions = computed(() =>
    this.actions()
      .filter(action => this.showOmInfo() || action.id !== ACTION_NOTIFY_OM) // hide notify OM action if not needed
      .map(action => ({ ...action, disabled: this.actionBusy.isBusy(action.id) }))
  );
  conflicts: Conflict[] = [];
  private subscriptions: Subscription[] = [];

  private translateService = inject(TranslateService);
  private projectService = inject(ProjectService);
  private branchService = inject(BranchService);
  private vehicleScheduleRefreshService = inject(VehicleScheduleRefreshService);
  private contextService = inject(ContextService);
  private planningHelperService = inject(PlanningHelperService);
  private cdr = inject(ChangeDetectorRef);
  private fleetService = inject(FleetService);
  private mapService = inject(MapService);
  private assignmentService = inject(AssignmentService);
  private notificationService = inject(NotificationService);

  constructor() {
    effect(() => {
      this.initializeActions();
    });
    effect(async () => {
      const p = this.project()();
      if (!p) return;

      // Always enable home route for a new project
      this.homeRoute = true;
      this.mapService.setHomeRoute(true);
      this.mapLoadedForProjectNo = undefined;
      this.routeDistances = [];
    });
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.mapService.markers$.subscribe(markers => {
        this.markers = [...markers];
        this.cdr.detectChanges(); // Trigger change detection
      })
    );
    this.subscriptions.push(
      this.mapService.routes$.subscribe({
        next: routes => {
          this.routes = [...routes];
          this.cdr.detectChanges(); // Trigger change detection
        },
      })
    );
    this.subscriptions.push(
      this.planningHelperService.onOpMManagementCheck().subscribe({
        next: async () => {
          await this.checkassignmentsTimeliness();
        },
      })
    );
    this.subscriptions.push(
      this.mapService.routeDistances$.subscribe({
        next: async distances => {
          this.routeDistances = distances.map(d => d);
          this.cdr.detectChanges(); // Trigger change detection
        },
      })
    );
    this.checkassignmentsTimeliness();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  initializeActions(): void {
    this.actions.set([
      {
        id: 1,
        text: this.translateService.instant('ACTIONS.EXPORT_PROJECT'),
        icon: 'export',
        onClick: this.exportCsv.bind(this, false),
      },
      {
        id: 2,
        text: this.translateService.instant('ACTIONS.EXPORT_PROJECT_WITH_RETURN'),
        icon: 'export',
        onClick: this.exportCsv.bind(this, true),
      },
      {
        id: ACTION_NOTIFY_DRIVER,
        text:
          this.project()()?.NotificationSend === NotificationState.Nothing
            ? this.translateService.instant('ACTIONS.NOTIFY_DRIVER_NEW')
            : this.translateService.instant('ACTIONS.NOTIFY_DRIVER_UPDATE'),
        icon: 'bell',
        onClick: () => {
          this.sendNotification();
        },
      },
      {
        id: ACTION_DELETE_PROJECT,
        text: this.translateService.instant('ACTIONS.DELETE_PROJECT'),
        icon: 'trash',
        onClick: () => {
          this.deleteProject();
        },
      },
      {
        id: ACTION_NOTIFY_OM,
        text: this.translateService.instant('ACTIONS.NOTIFY_OM'),
        icon: 'bell',
        onClick: this.notifyOm.bind(this),
      },
    ]);

    this.cdr.detectChanges();
  }

  async checkassignmentsTimeliness() {
    if (!this.connectionString) {
      this.connectionString = await firstValueFrom(this.fleetService.getMapsConnectionKey());
    }
    // load all assignments for the project and verify timeliness
    this.showOmInfo.set(false);
    this.conflicts = [];
    const loadunits = this.projectService.getProjectLoadunits(this.project()()!.ProjectNo);
    loadunits.subscribe({
      next: async loadunits => {
        // console.log('Total load units:', loadunits.length); // Log the number of load units
        this.showOmInfo.set(false);
        for (const lu of loadunits) {
          const extendedData = await firstValueFrom(
            this.assignmentService.getAssignmentSubtableData(lu.AssignmentNo, lu.Type)
          );
          // console.log('Processing load unit:', lu);
          const conflict = await this.planningHelperService.verifyTimeliness(
            this.project()()!,
            {
              AssignmentNo: lu.AssignmentNo,
              Type: lu.Type,
              EventDate: lu.EventDate ?? '',
            },
            new Date(extendedData.DispoVon ?? ''),
            new Date(extendedData.DispoBis ?? '')
          );
          if (!conflict.isvalid || conflict.checkNeeded) {
            this.showOmInfo.set(true);
            this.conflicts.push(conflict); // bei VA Datum Vorgezogene Abholung
            continue;
          }
        }
        // remove duplicates
        this.conflicts = this.conflicts.filter((v, i, a) => a.findIndex(t => t.AssignmentNo === v.AssignmentNo) === i);
        this.cdr.detectChanges();
      },
    });
  }

  async sendNotification() {
    if (this.actionBusy.isBusy(ACTION_NOTIFY_DRIVER)) return;
    const project = this.project()();
    if (project) {
      notify({ message: this.translateService.instant('ACTIONS.NOTIFICATION_SENDING'), shading: false }, 'info', 3000);
      if (project.DriverId !== undefined) {
        await this.actionBusy.run(ACTION_NOTIFY_DRIVER, async () => {
          try {
            await firstValueFrom(this.fleetService.sendNotification(project.ProjectNo));
            notify({ message: this.translateService.instant('ACTIONS.NOTIFICATION_SENT') }, 'success', 5000);
            this.changed.emit('reload');
          } catch (error) {
            console.error('Error sending notification:', error);
            notify({ message: this.translateService.instant('ERROR.NOTIFICATION') }, 'error', 5000);
          }
        });
      }
    } else {
      console.error('Project is undefined');
    }
  }

  async deleteProject() {
    if (this.actionBusy.isBusy(ACTION_DELETE_PROJECT)) return;
    const project = this.project();
    if (!project() || !confirm(this.translateService.instant('PROJECT.ASK_DELETE'))) return;
    await this.actionBusy.run(ACTION_DELETE_PROJECT, async () => {
      try {
        await firstValueFrom(this.projectService.deleteProject(project()!.ProjectNo));
      } catch (err) {
        console.error('delete project error', err);
        let message: string = 'PROJECT.DELETE_FAIL.Unknown';
        if (err && typeof err === 'object' && 'error' in err && typeof err.error === 'string') {
          message = 'PROJECT.DELETE_FAIL.' + err.error;
        }
        notify({ message: this.translateService.instant(message), shading: false }, 'error', 3000);
        return;
      }
      notify({ message: this.translateService.instant('PROJECT.DELETE_SUCCESS'), shading: false }, 'success', 3000);
      this.contextService.closeContextView();
      this.vehicleScheduleRefreshService.triggerRefresh();
    });
  }

  private mapToConflictDto(conflict: Conflict): ConflictDto {
    let conflictType: ConflictType;

    switch (conflict.reason) {
      case 'Extend':
        conflictType = 'Extend';
        break;
      case 'MoveForward':
        conflictType = 'MoveForward';
        break;
      default:
        conflictType = 'NotDefined';
        break;
    }

    return {
      AssignmentNo: conflict.AssignmentNo,
      CheckNeeded: conflict.checkNeeded,
      Validity: conflict.isvalid,
      ConflictType: conflictType,
    };
  }

  async notifyOm() {
    if (this.actionBusy.isBusy(ACTION_NOTIFY_OM)) return;
    const confirmed = confirm(this.translateService.instant('PROJECT.ASK_OM_NOTIFICATION'));
    if (!confirmed) return;

    const project = this.project()();
    if (!project) {
      console.error('Project is undefined');
      return;
    }
    const conflictDtos = this.conflicts.map(c => this.mapToConflictDto(c));
    await this.actionBusy.run(ACTION_NOTIFY_OM, async () => {
      try {
        await firstValueFrom(this.projectService.sendNotificationOM(project.ProjectNo, conflictDtos));
        notify(
          { message: this.translateService.instant('PROJECT.NOTIFY_OM_SUCCESS'), shading: false },
          'success',
          3000
        );
      } catch (err) {
        console.error('notify om error', err);
        notify({ message: this.translateService.instant('PROJECT.NOTIFY_OM_FAIL'), shading: false }, 'error', 3000);
      }
    });
  }

  async exportCsv(includeReturnRoute: boolean) {
    const separator = ';';
    const eol = '\r\n';

    const project = this.project();
    const id = project()?.ProjectNo;
    if (id === undefined) return;
    const loadunits = await firstValueFrom(this.projectService.getProjectLoadunits(id));

    // We also add the branch address to the front and end of the csv
    const branchAddress = await firstValueFrom(this.branchService.getHomeAddress());
    const branchAddressString = `${this.escapeField('NicLen')}${separator}${this.escapeField(branchAddress.Street)}${separator}${this.escapeField(branchAddress.PostalCode)}${separator}${this.escapeField(branchAddress.City)}`;

    const lines = new Array<string>();
    lines.push(`Name${separator}Strasse${separator}PLZ${separator}Ort${eol}`);

    lines.push(branchAddressString + eol);

    let previousAddress: string | null = null;
    for (const loadunit of loadunits) {
      // If there are consecutive loadunits with the same station (defined as street, zipcode and city), we only want to show the first entry
      const address = `${this.escapeField(loadunit.ActionStreet)}${separator}${this.escapeField(loadunit.ActionZipCode)}${separator}${this.escapeField(loadunit.ActionCity)}`;
      if (previousAddress != null && address == previousAddress) {
        continue;
      }
      previousAddress = address;

      lines.push(
        `${this.escapeField(loadunit.Customer)}${separator}${this.escapeField(loadunit.ActionStreet)}${separator}${this.escapeField(loadunit.ActionZipCode)}${separator}${this.escapeField(loadunit.ActionCity)}${eol}`
      );
    }

    if (includeReturnRoute) {
      lines.push(branchAddressString + eol);
    }

    const csvContent = lines.join('');
    const bom = '\uFEFF';
    const csvWithBom = bom + csvContent;
    const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${new Date().toJSON().slice(0, 10)}_${project()?.ProjectNo}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  escapeField(field: string | undefined): string {
    if (!field) return '';

    return `"${field.split('"').join('""')}"`;
  }
}
